import { ValidationError } from '@domain/errors';
import type { Article, ArticleDraft } from '@domain/entities';
import type {
  ArticleQuery,
  ArticleRepository,
  NotificationRepository,
} from '@domain/repositories';
import type { Page, PublicationStatus } from '@domain/value-objects';
import type { AccessPolicy } from '../../ports';
import type { UseCase } from '../use-case';

export class SearchArticles implements UseCase<ArticleQuery, Page<Article>> {
  constructor(private readonly repo: ArticleRepository) {}
  execute(query: ArticleQuery): Promise<Page<Article>> {
    return this.repo.search(query);
  }
}

export interface SaveArticleInput {
  /** null => création. */
  id: string | null;
  draft: ArticleDraft;
}

export class SaveArticle implements UseCase<SaveArticleInput, Article> {
  constructor(private readonly repo: ArticleRepository) {}

  async execute({ id, draft }: SaveArticleInput): Promise<Article> {
    if (!draft.title.trim()) {
      throw new ValidationError('Formulaire incomplet', { title: 'Le titre est requis' });
    }
    const normalized: ArticleDraft = { ...draft, title: draft.title.trim() };
    return id === null ? this.repo.create(normalized) : this.repo.update(id, normalized);
  }
}

export interface SetArticleStatusInput {
  ids: readonly string[];
  status: PublicationStatus;
}

/** Publication / dépublication ; notifie les utilisatrices à la publication. */
export class SetArticleStatus implements UseCase<SetArticleStatusInput, Article[]> {
  constructor(
    private readonly repo: ArticleRepository,
    private readonly notifications: NotificationRepository,
  ) {}

  async execute({ ids, status }: SetArticleStatusInput): Promise<Article[]> {
    if (ids.length === 0) {
      throw new ValidationError('Aucun article sélectionné');
    }
    const updated = await this.repo.setStatus(ids, status);
    if (status === 'published') {
      await Promise.all(
        updated.map((article) =>
          this.notifications.append('articlePublished', '—', article.title),
        ),
      );
    }
    return updated;
  }
}

/** Suppression définitive : réservé au super-admin. */
export class DeleteArticle implements UseCase<string, void> {
  constructor(
    private readonly repo: ArticleRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer un article');
    await this.repo.remove(id);
  }
}
